import { INTEGRITY_EVENT_KINDS, INTEGRITY_PRESENCE_PENALTY } from "./schemas.js";
/** A clean, fully observed session. Not 100: the figure is observational, not a compliment. */
export const PRESENCE_BASELINE = 92;
export const PRESENCE_FLOOR = 30;
export const PRESENCE_CEILING = 95;
/**
 * The kinds that can only mean something if a face detector genuinely ran. `face_watch_on` is the
 * proof that it did; the other three are its findings.
 */
const CAMERA_DERIVED = new Set([
    "face_watch_on",
    "no_face",
    "second_face",
    "face_visible",
]);
/** Same admissibility contract for the gaze trio: `gaze_watch_on` is the proof the estimator
 *  genuinely ran; without it a `gaze_away` row may never cost a point (only be shown, labelled). */
const GAZE_DERIVED = new Set([
    "gaze_watch_on",
    "gaze_away",
    "gaze_return",
]);
/**
 * Score presence from the per-kind event counts of one session.
 *
 * `counts` is the raw `kind → n` map straight out of the events table, so unknown keys (rows
 * written by an older client before a rename) are ignored rather than crashing the report.
 *
 * NO CAMERA READING, NO CAMERA PENALTY. A camera finding is only admissible when `face_watch_on`
 * proves a detector produced a real reading from a real frame. Without it, `no_face` rows are not
 * evidence of anybody's absence — they are evidence of OUR blindness, and a build that sampled a
 * blank <video> from a denied camera wrote exactly that, once every five seconds, for candidates
 * who were sitting there the whole time. Those rows are in the database now; this rule is what
 * stops them costing anyone a point, today and on every report re-run of an old candidacy. The
 * events are still SHOWN to the reviewer beside an explicit "camera presence was not measured",
 * because deleting them would be its own kind of dishonesty.
 */
export function computePresence(counts) {
    const cameraObserved = (counts.face_watch_on ?? 0) > 0;
    const gazeObserved = (counts.gaze_watch_on ?? 0) > 0;
    // "Did we hear from this client at all?" — any kind counts, including the zero-penalty
    // heartbeats. Old candidacies predating `proctor_on` still have tab/paste rows, so they keep
    // the score they always had; only a genuinely empty channel reads as unknown.
    let total = 0;
    let penalty = 0;
    for (const kind of INTEGRITY_EVENT_KINDS) {
        const n = counts[kind] ?? 0;
        if (n <= 0)
            continue;
        total += n;
        if (CAMERA_DERIVED.has(kind) && !cameraObserved)
            continue; // reported, but inadmissible
        if (GAZE_DERIVED.has(kind) && !gazeObserved)
            continue; // same rule, same reason
        penalty += n * INTEGRITY_PRESENCE_PENALTY[kind];
    }
    const basis = { browser: total > 0, camera: cameraObserved, gaze: gazeObserved };
    if (total === 0)
        return { score: null, basis };
    return {
        score: Math.max(PRESENCE_FLOOR, Math.min(PRESENCE_CEILING, PRESENCE_BASELINE - penalty)),
        basis,
    };
}
export function formatPresenceReturn(from, ms) {
    return `${from}:${Math.max(0, Math.round(ms / 1000))}`;
}
export function parsePresenceReturn(detail) {
    if (!detail)
        return null;
    const [from, secs] = detail.split(":");
    if (from !== "absent" && from !== "multiple")
        return null;
    const seconds = Number(secs);
    if (!Number.isFinite(seconds) || seconds < 0)
        return null;
    return { from, seconds };
}
/**
 * `gaze_return` mirrors `face_visible`: the receipt carries how long the attention episode lasted,
 * so the report reads "looked away from the screen twice while speaking, 41s in total" instead of
 * a bare count. Shared for the same reason as the presence pair — a detail string written on one
 * side and parsed on the other must have exactly one definition.
 */
export function formatGazeReturn(ms) {
    return `away:${Math.max(0, Math.round(ms / 1000))}`;
}
export function parseGazeReturn(detail) {
    if (!detail)
        return null;
    const [tag, secs] = detail.split(":");
    if (tag !== "away")
        return null;
    const seconds = Number(secs);
    if (!Number.isFinite(seconds) || seconds < 0)
        return null;
    return { seconds };
}
/**
 * `strict_resume` mirrors the same receipt discipline: "<reason>:<secondsPaused>", written by the
 * candidate runtime and parsed by the recruiter report. One definition here, or the two ends
 * drift exactly the way the presence vocabulary once did.
 */
export function formatStrictResume(reason, ms) {
    return `${reason}:${Math.max(0, Math.round(ms / 1000))}`;
}
export function parseStrictResume(detail) {
    if (!detail)
        return null;
    const i = detail.lastIndexOf(":");
    if (i <= 0)
        return null;
    const seconds = Number(detail.slice(i + 1));
    if (!Number.isFinite(seconds) || seconds < 0)
        return null;
    return { reason: detail.slice(0, i), seconds };
}
//# sourceMappingURL=integrity.js.map